import type { jsPDF } from 'jspdf';

/**
 * Schrift des Begehungsprotokolls: Inter, dieselbe Schrift wie in der App.
 *
 * Drei Schnitte, je als eigene Familie registriert, weil jsPDF pro Familie nur
 * normal/bold/italic kennt und das Protokoll mit Regular, Medium und SemiBold
 * gestuft ist:
 *   'Inter'          normal  → Fliesstext
 *   'Inter-Medium'   normal  → Werte, Firmennamen, Tabellenzeilen
 *   'Inter-SemiBold' normal  → Ueberschriften, Nummern, Plaketten
 *
 * Laedt die Schrift nicht (Netz weg beim Nachladen des Chunks), entsteht das
 * PDF mit Helvetica — `reportFontFaces` liefert dann die Ersatzzuordnung.
 * Ein Bericht ohne Inter ist haesslicher, einer, der gar nicht entsteht,
 * waere ein Ausfall.
 *
 * Nebenwirkung, die man kennen muss: Text in einer eingebetteten Schrift
 * steht im Seiteninhalt als Glyphen-Nummern, nicht als lesbare Zeichenkette.
 * Die Generator-Tests, die den Rohinhalt nach Woertern durchsuchen, schalten
 * die Schrift deshalb per `vi.mock('./reportFont')` ab. Durchsuchen und
 * Kopieren im PDF-Betrachter funktioniert trotzdem (jsPDF schreibt eine
 * ToUnicode-Tabelle mit).
 */

interface Faces {
    regular: string;
    medium: string;
    semibold: string;
}

/** Schriftrolle im Generator → [Familie, Schnitt] fuer doc.setFont. */
export type ReportFontRole = 'regular' | 'medium' | 'semibold';
export type ReportFontFaces = Record<ReportFontRole, [string, string]>;

const INTER_FACES: ReportFontFaces = {
    regular: ['Inter', 'normal'],
    medium: ['Inter-Medium', 'normal'],
    semibold: ['Inter-SemiBold', 'normal'],
};

const HELVETICA_FACES: ReportFontFaces = {
    regular: ['helvetica', 'normal'],
    medium: ['helvetica', 'normal'],
    semibold: ['helvetica', 'bold'],
};

const registered = new WeakSet<object>();
let cache: Promise<Faces> | null = null;
let loadedFaces: Faces | null = null;

/**
 * Laedt die Base64-Daten (zusammen rund 300 kB) als eigenen Chunk — erst beim
 * ersten Protokoll-PDF, danach aus dem Speicher.
 */
const loadFaces = (): Promise<Faces> => {
    if (!cache) {
        cache = Promise.all([
            import('./fonts/interRegular.js'),
            import('./fonts/interMedium.js'),
            import('./fonts/interSemiBold.js'),
        ]).then(([r, m, s]) => ({
            regular: r.INTER_REGULAR_BASE64,
            medium: m.INTER_MEDIUM_BASE64,
            semibold: s.INTER_SEMIBOLD_BASE64,
        }));
        // Ein gescheiterter Versuch darf den naechsten nicht blockieren.
        cache.catch(() => { cache = null; });
    }
    return cache;
};

/** Laedt die Schrift; false = das PDF entsteht mit Helvetica. */
export const ensureReportFont = async (): Promise<boolean> => {
    try {
        loadedFaces = await loadFaces();
        return true;
    } catch (e) {
        console.error('Die Protokollschrift (Inter) konnte nicht geladen werden. Das PDF entsteht mit Helvetica.', e);
        return false;
    }
};

/**
 * Registriert die Schnitte im Dokument und liefert die Zuordnung der
 * Schriftrollen. Ohne geladene Daten: Helvetica.
 */
export const reportFontFaces = (doc: jsPDF): ReportFontFaces => {
    if (registered.has(doc as unknown as object)) return INTER_FACES;
    if (!loadedFaces) return HELVETICA_FACES;
    try {
        doc.addFileToVFS('Inter-Regular.ttf', loadedFaces.regular);
        doc.addFont('Inter-Regular.ttf', 'Inter', 'normal');
        doc.addFileToVFS('Inter-Medium.ttf', loadedFaces.medium);
        doc.addFont('Inter-Medium.ttf', 'Inter-Medium', 'normal');
        doc.addFileToVFS('Inter-SemiBold.ttf', loadedFaces.semibold);
        doc.addFont('Inter-SemiBold.ttf', 'Inter-SemiBold', 'normal');
    } catch (e) {
        console.error('Die Protokollschrift (Inter) liess sich nicht registrieren. Das PDF entsteht mit Helvetica.', e);
        return HELVETICA_FACES;
    }
    registered.add(doc as unknown as object);
    return INTER_FACES;
};
